/**
 * The DislpayMemberDetails-Component displays details of a member
 */
import React, { useReducer, useState, useEffect } from "react";
import {
  Checkbox,
  Grid,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  createFilterOptions,
  Autocomplete,
  Modal,
  Paper,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  useTheme,
  Box,
} from "@mui/material";
import { ExpandLess, ExpandMore, AddCircleOutline, Clear, Add } from "@mui/icons-material";
import { Link } from "react-router-dom";
import dayjs, { Dayjs } from "dayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { transformToGermanDate } from "../../../utils/dateUtils";
import { showErrorMessage, showSuccessMessage } from "../../../utils/toastUtils";
import { doesPermissionsHaveSomeOf } from "../../../utils/authUtils";
import {
  formatPhoneNumber,
  getInternationalPhoneValidationMessage,
  isValidInternationalPhoneNumber,
  sanitizeInternationalPhoneNumber,
} from "../../../utils/phoneUtils";
import MemberImage from "../../../components/general/MemberImage";
import InfoCard from "../../../components/general/InfoCard";
import * as membersTypes from "../../../types/membersTypes";
import * as globalTypes from "../../../types/globalTypes";
import JBTLogoBlack from "../../../assets/jbt-logo-black.png";
import { checkBic, checkIban } from "../../../utils/financeUtils";
import { AxiosResponse } from "axios";

interface DisplayMemberDetailsProps {
  members: membersTypes.MemberPartialDto[];
  listOfPermissions: globalTypes.Permission[];
  departments: membersTypes.DepartmentPartialDto[];
  listOfLanguages: membersTypes.Language[];
  listOfItSkills: membersTypes.ItSkill[];
  memberDetails: membersTypes.MemberDetailsDto;
  isOwner: boolean;
  memberImage: membersTypes.MemberImage | undefined;
  memberDirectorPositions: membersTypes.MemberDirectorPositionsDto[];
  directorPositions: membersTypes.DirectorPositionDto[];
  updateMemberDetails: (data: membersTypes.MemberDetailsDto) => Promise<AxiosResponse<membersTypes.MemberDetailsDto>>;
  saveMemberImage: (file: File) => void;
  deleteDirectorPosition: (directorId: number) => void;
  addDirectorPosition: (directorID: number, from: Date, until: Date) => void;
  changeDirectorPosition: (directorId: number, from: Date, until: Date) => void;
}

const sanitizeSmartphoneInput = (value: string): string => {
  if (value === "+") {
    return value;
  }

  return sanitizeInternationalPhoneNumber(value);
};

/**
 * Displays the member details
 */
const DisplayMemberDetails: React.FunctionComponent<DisplayMemberDetailsProps> = (props: DisplayMemberDetailsProps) => {
  const {
    members,
    departments,
    listOfLanguages,
    listOfItSkills: listOfEDVSkills,
    memberDetails,
    memberDirectorPositions,
    directorPositions,
    deleteDirectorPosition,
    addDirectorPosition,
    changeDirectorPosition,
  } = props;
  const theme = useTheme();
  const filteredMembers = members.filter((member) => member.memberId !== memberDetails.memberId);

  /**
   * Function which proivdes the styles of the MemberPage
   */
  const styles = {
    displayMemberDetailsRoot: {
      flexGrow: 1,
    },
    imageSection: {
      display: "flex",
      alignItems: "center",
    },
    imageSectionText: {
      marginLeft: "8px",
    },
    imageEdit: {
      height: "100%",
      marginTop: "50px",
      display: "flex",
      flexDirection: "column",
      justifyContent: "flex-start",
    },
    category: {
      color: theme.palette.text.secondary,
      width: "100%",
    },
    categoryHeader: {
      display: "flex",
      width: "100%",
      justifyContent: "space-between",
      alignItems: "center",
    },
    categoryTitlePaper: {
      backgroundColor: theme.palette.secondary.main,
      marginTop: "-16px",
      marginLeft: "-17px",
      marginRight: "-17px",
      paddingTop: "10px",
      paddingBottom: "10px",
      borderBottomRightRadius: "0px",
      borderBottomLeftRadius: "0px",
    },
    categoryTitle: {
      textAlign: "center",
    },
    categoryLine: {
      paddingTop: "12.5px",
      paddingBottom: "11.5px",
      textAlign: "right",
    },
    expandCategoryTitlePaper: {
      borderTopRightRadius: "5px",
      borderTopLeftRadius: "5px",
      backgroundColor: theme.palette.secondary.main,
    },
    expandCategoryTitle: {
      display: "flex",
      justifyContent: "center",
    },
    subCategoryHeader: {
      display: "flex",
      alignItems: "center",
      gap: 1,
    },
    categoryItem: {
      display: "flex",
      justifyContent: "space-between",
      [theme.breakpoints.up("md")]: {
        width: "30%",
      },
      [theme.breakpoints.down("xl")]: {
        width: "50%",
      },
      [theme.breakpoints.down("lg")]: {
        width: "60%",
      },
      [theme.breakpoints.down("md")]: {
        width: "100%",
      },
    },
    jbtEmailItem: {
      display: "flex",
      justifyContent: "space-between",
      width: "max-content",
      maxWidth: "100%",
    },
    categoryItemList: {
      display: "flex",
      flexDirection: "column",
      alignItems: "right",
    },
    addListItemBtn: {
      display: "flex",
      alignContent: "center",
    },
    hr: {
      display: "block",
      height: "1px",
      border: 0,
      borderTop: "1px solid #ccc",
      padding: 0,
    },
    categorySubItem: {
      inBottom: "-6px",
    },
    workshopItem: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      width: "80%",
    },
    fullWidth: {
      width: "100%",
    },
    dialogListItem: {
      display: "flex",
    },
    dialogItemAddBtn: {
      display: "flex",
      alignContent: "center",
    },
    dialogContent: {
      // Outlined labels sit above the field and Grid spacing pulls the first field up.
      // Extra top padding keeps that label inside the scrolling dialog content.
      paddingTop: theme.spacing(3),
    },
    dialogFormGrid: {
      paddingTop: theme.spacing(1),
    },
    submitContainer: {
      width: "100%",
      display: "flex",
      alignItems: "stretch",
      justifyContent: "flex-end",
      gap: theme.spacing(1.5),
      [theme.breakpoints.down("sm")]: {
        flexDirection: "column-reverse",
      },
    },
    cancelButton: {
      margin: 0,
      minHeight: "44px",
      minWidth: "140px",
      paddingLeft: theme.spacing(3),
      paddingRight: theme.spacing(3),
      backgroundColor: theme.palette.grey[300],
      color: theme.palette.text.primary,
      boxShadow: "none",
      whiteSpace: "nowrap",
      "&:hover": {
        backgroundColor: theme.palette.grey[400],
        boxShadow: "none",
      },
      [theme.breakpoints.down("sm")]: {
        width: "100%",
      },
    },
    submitButton: {
      margin: 0,
      minHeight: "44px",
      minWidth: "220px",
      paddingLeft: theme.spacing(3),
      paddingRight: theme.spacing(3),
      color: "white",
      [theme.breakpoints.down("sm")]: {
        width: "100%",
      },
    },
    vorstandSubmitButton: {
      margin: theme.spacing(1, 1, 0, 0),
      maxHeight: "37px",
    },
    vorstandCancelButton: {
      margin: theme.spacing(1, 0, 0, 0),
      maxHeight: "37px",
    },
    tableSecondLastRow: {
      borderBottomWidth: "1px",
      borderBottomColor: theme.palette.primary.main,
      borderBottomStyle: "solid",
    },
    modalPaper: {
      overflowY: "auto",
      position: "absolute",
      margin: "auto",
      top: "20%",
      left: "10%",
      right: "10%",
      bottom: "20%",
      width: "60%",
      display: "flex",
      justifyContent: "center",
      boxShadow: theme.shadows[5],
      padding: theme.spacing(2, 4, 3),
    },
    tableContainer: {
      maxHeight: (window.screen.height - 75) * 0.8,
    },
    tableHeadCell: {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.primary.contrastText,
    },
    paperHeaderText: {
      marginLeft: theme.spacing(1),
      marginTop: theme.spacing(1),
    },
  };

  type EditedMemberDirectorPositions = membersTypes.MemberDirectorPositionsDto & { delete: boolean };

  // Filter of languages for the autocomplete component
  const langFilter = createFilterOptions<membersTypes.Language>();

  // Filter of languages for the autocomplete component
  const itFilter = createFilterOptions<membersTypes.ItSkill>();

  const [isEditingDirectorPosition, setIsEditingDirectorPosition] = useState(false);
  const [editedMemberDirectorPositions, setEditedMemberDirectorPositions] = useState<EditedMemberDirectorPositions[]>(
    []
  );
  const [fromValue, setFromValue] = useState<Dayjs | null>(dayjs());
  const [untilValue, setUntilValue] = useState<Dayjs | null>(dayjs());
  const [directorPosition, setDirectorPosition] = useState(-1);

  const [careerOpen, setCareerOpen] = useState(false);
  const [lastname] = useState(memberDetails.lastname);
  const [name] = useState<string>(memberDetails.firstname);
  const [birthday, setBirthday] = useState<Date | null>(memberDetails.birthday);
  const [smartphone, setSmartphone] = useState<string>(formatPhoneNumber(memberDetails.mobile));
  const [jbtMail, setJbtMail] = useState<string>(memberDetails.jbtEmail ?? "");
  const [memberState] = useState<membersTypes.MemberStatus>(memberDetails.memberStatus);
  const [department, setDepartment] = useState<membersTypes.DepartmentPartialDto | null>(memberDetails.department);
  const [departmentError, setDepartmentError] = useState(false);
  const [mentorState, setMentorState] = useState<membersTypes.MentorDto | null>(
    memberDetails.mentor ? memberDetails.mentor : null
  );
  const [employer, setEmployer] = useState<string>(memberDetails.employer ? memberDetails.employer : "");
  const [street1, setStreet1] = useState<string>(memberDetails.street1 ? memberDetails.street1 : "");
  const [plz1State, setPlz1State] = useState<string>(
    memberDetails.postalCode1 ? memberDetails.postalCode1.toString() : ""
  );
  const [placeOfResidence1, setPlaceOfResidence1] = useState<string>(memberDetails.city1 ? memberDetails.city1 : "");
  const [telephone1] = useState<string>(memberDetails.phone1 ? memberDetails.phone1.toString() : "");
  const [email1State, setEmail1State] = useState<string>(memberDetails.email1 || "");
  const [street2, setStreet2] = useState<string>(memberDetails.street2 ? memberDetails.street2 : "");
  const [plz2State, setPlz2State] = useState<string>(
    memberDetails.postalCode2 ? memberDetails.postalCode2.toString() : ""
  );
  const [placeOfResidence2, setPlaceOfResidence2] = useState<string>(memberDetails.city2 ? memberDetails.city2 : "");
  const [telephone2, setTelephone2] = useState<string>(memberDetails.phone2 ? memberDetails.phone2.toString() : "");
  const [email2State, setEmail2State] = useState<string>(memberDetails.email2 ? memberDetails.email2 : "");
  const [university, setUniversity] = useState<string>(memberDetails.university ? memberDetails.university : "");
  const [courseOfStudy, setCourseOfStudy] = useState<string>(
    memberDetails.courseOfStudy ? memberDetails.courseOfStudy : ""
  );
  const [startOfStudy, setStartOfStudy] = useState<Date | null>(memberDetails.studyStart);
  const [endOfStudy, setEndOfStudy] = useState<Date | null>(memberDetails.studyEnd);
  const [speciality, setSpeciality] = useState<string>(
    memberDetails.specializations ? memberDetails.specializations : ""
  );
  const [apprenticeship, setApprenticeship] = useState<string>(
    memberDetails.apprenticeship ? memberDetails.apprenticeship : ""
  );
  const [accountHolder, setAccountHolder] = useState<string>(
    memberDetails.accountHolder ? memberDetails.accountHolder : ""
  );
  const [ibanState, setIbanState] = useState<string>(memberDetails?.iban || "");
  const [bicState, setBicState] = useState<string>(memberDetails?.bic || "");
  const [commitmentState] = useState<string>(memberDetails.commitment ? memberDetails.commitment : "");
  const [driversLicense] = useState<boolean>(memberDetails.drivingLicense > 0);
  const [firstAid] = useState<boolean>(memberDetails.firstAidTraining);
  const [traineeSince, setTraineeSince] = useState<Date | null>(memberDetails.traineeSince);
  const [memberSince, setMemberSince] = useState<Date | null>(memberDetails.memberSince);
  const [seniorSince, setSeniorSince] = useState<Date | null>(memberDetails.seniorSince);
  const [alumniSince, setAlumniSince] = useState<Date | null>(memberDetails.alumnusSince);
  const [passiveSince, setPassiveSince] = useState<Date | null>(memberDetails.passiveSince);
  const [generalInfoDialogOpen, setGeneralInfoDialogOpen] = useState<boolean>(false);
  const [clubInfoDialogOpen, setClubInfoDialogOpen] = useState<boolean>(false);
  const [studyInfoDialogOpen, setStudyInfoDialogOpen] = useState<boolean>(false);
  const [paymentInfoDialogOpen, setPaymentInfoDialogOpen] = useState<boolean>(false);
  const [qualificationInfoDialogOpen, setQualificationInfoDialogOpen] = useState<boolean>(false);
  const [privateAddressInfoDialogOpen, setPrivateAddressInfoDialogOpen] = useState<boolean>(false);
  const [menteeList] = useState<membersTypes.MenteeDto[]>(memberDetails?.mentees || []);

  /**
   * Saves the changes of the image
   * @param event ChangeEvent
   */
  const saveImage = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];
      props.saveMemberImage(file);
    }
  };

  /**
   * Checks if there would be a duplicate value in languages if value would be added
   * @param list the list of the languages of the member
   * @param value the new value that should be inserted
   * @returns true it there would be a duplicate
   */
  const checkLanguagesForDuplicates = (list: membersTypes.LanguageOfMember[], value: string) => {
    return list.filter((language) => language.value === value).length !== 0;
  };

  /**
   * Updates the list of languages of the member
   * @param list the current list of languages of the member
   * @param index the index of the language that should be updated
   * @param value the new value of the language
   * @param typeOfValue specifies which type the value is and what attribute of language should be updated
   * @returns An updated list of languages
   */
  const updateLanguages = (
    list: membersTypes.LanguageOfMember[],
    index: number,
    value: string | number,
    typeOfValue: string
  ) => {
    const newList = [...list];
    switch (typeOfValue) {
      case "WERT": {
        const updatedLanguage: membersTypes.LanguageOfMember = {
          ...list[index],
          value: value as string,
        };
        newList[index] = updatedLanguage;
        return newList;
      }
      case "NIVEAU": {
        const updatedLanguage: membersTypes.LanguageOfMember = {
          ...list[index],
          level: Number(value), // Ensure it's a number
        };
        newList[index] = updatedLanguage;
        return newList;
      }
      default:
        return list;
    }
  };

  /**
   * The reducer function for the languages
   * It manages how the languages can be changed
   * @param state the current state of the languages
   * @param action the action that should be performed
   * @returns the new state of the languages
   */
  const languagesReducer = (state: membersTypes.LanguageOfMember[], action: membersTypes.languagesReducerAction) => {
    switch (action.type) {
      case membersTypes.languagesReducerActionType.addNewLanguageWithValueAsString: {
        if (checkLanguagesForDuplicates(state, action.payload.value)) {
          return state;
        }
        return updateLanguages(state, action.payload.index, action.payload.value, "WERT");
      }
      case membersTypes.languagesReducerActionType.addNewLanguageWithValueAsObject: {
        if (action.payload.lang.inputValue) {
          if (checkLanguagesForDuplicates(state, action.payload.lang.inputValue)) {
            return state;
          }
          return updateLanguages(state, action.payload.index, action.payload.lang.inputValue, "WERT");
        } else if (action.payload.lang.value) {
          if (checkLanguagesForDuplicates(state, action.payload.lang.value)) {
            return state;
          }
          return updateLanguages(state, action.payload.index, action.payload.lang.value, "WERT");
        } else {
          return state;
        }
      }
      case membersTypes.languagesReducerActionType.addNewLanguageWithNiveau: {
        return updateLanguages(state, action.payload.index, Number(action.payload.niveau), "NIVEAU"); // Ensure it's a number
      }
      case membersTypes.languagesReducerActionType.addEmptyLanguage: {
        return [...state, { memberId: memberDetails.memberId, value: "", level: 0 }]; // Initialize level as a number
      }
      case membersTypes.languagesReducerActionType.deleteLanguage: {
        return state.filter((value) => !(value.value === action.payload.lang.value));
      }
      default:
        return state;
    }
  };

  /**
   * Checks if there would be a duplicate value in edv skills if value would be added
   * @param list the list of the edv skills of the member
   * @param value the new value that should be inserted
   * @returns true it there would be a duplicate
   */
  const checkEdvSkillsForDuplicates = (list: membersTypes.ItSkillOfMember[], value: string) => {
    return list.filter((itSkill) => itSkill.value === value).length !== 0;
  };

  /**
   * Updates the list of edv skills of the member
   * @param list the current list of edv skills of the member
   * @param index the index of the edv skill that should be updated
   * @param value the new value of the edv skill
   * @param typeOfValue specifies which type the value is and what attribute of edv skill should be updated
   * @returns An updated list of languages
   */
  const updateEdvSkills = (
    list: membersTypes.ItSkillOfMember[],
    index: number,
    value: string | number,
    typeOfValue: string
  ) => {
    const newList = [...list];
    switch (typeOfValue) {
      case "WERT": {
        const updatedEdvSkill: membersTypes.ItSkillOfMember = {
          ...list[index],
          value: value as string,
        };
        newList[index] = updatedEdvSkill;
        return newList;
      }
      case "NIVEAU": {
        const updatedEdvSkill: membersTypes.ItSkillOfMember = {
          ...list[index],
          level: Number(value), // Ensure it's a number
        };
        newList[index] = updatedEdvSkill;
        return newList;
      }
      default:
        return list;
    }
  };

  /**
   * The reducer function for the edvSkills
   * It manages how the edvSkills can be changed
   * @param state the current state of the edvSkills
   * @param action the action that should be performed
   * @returns the new state of the edvSkills
   */
  const itSkillsReducer = (state: membersTypes.ItSkillOfMember[], action: membersTypes.itSkillsReducerAction) => {
    switch (action.type) {
      case membersTypes.itSkillsReducerActionType.addNewItSkillWithValueAsString: {
        if (checkEdvSkillsForDuplicates(state, action.payload.value)) {
          return state;
        }
        return updateEdvSkills(state, action.payload.index, action.payload.value, "WERT");
      }
      case membersTypes.itSkillsReducerActionType.addNewItSkillWithValueAsObject: {
        if (action.payload.itSkill.inputValue) {
          if (checkEdvSkillsForDuplicates(state, action.payload.itSkill.inputValue)) {
            return state;
          }
          return updateEdvSkills(state, action.payload.index, action.payload.itSkill.inputValue, "WERT");
        } else if (action.payload.itSkill.value) {
          if (checkEdvSkillsForDuplicates(state, action.payload.itSkill.value)) {
            return state;
          }
          return updateEdvSkills(state, action.payload.index, action.payload.itSkill.value, "WERT");
        } else {
          return state;
        }
      }
      case membersTypes.itSkillsReducerActionType.addNewItSkillWithNiveau: {
        return updateEdvSkills(state, action.payload.index, Number(action.payload.level), "NIVEAU"); // Ensure it's a number
      }
      case membersTypes.itSkillsReducerActionType.addEmptyItSkill: {
        return [...state, { memberId: memberDetails.memberId, value: "", level: 0 }]; // Initialize level as a number
      }
      case membersTypes.itSkillsReducerActionType.deleteItSkill: {
        return state.filter((value) => !(value.value === action.payload.itSkill.value));
      }
      default:
        return state;
    }
  };

  const [languages, dispatchLanguages] = useReducer(languagesReducer, memberDetails?.languages || []);
  const [itSkills, dispatchItSkills] = useReducer(itSkillsReducer, memberDetails?.itSkills || []);

  useEffect(() => {
    setEditedMemberDirectorPositions(props.memberDirectorPositions.map((position) => ({ ...position, delete: false })));
  }, [props.memberDirectorPositions]);

  /**
   * Copies the saved general information into the dialog fields.
   */
  const resetGeneralInfoFields = () => {
    setSmartphone(memberDetails.mobile ?? "");
    setJbtMail(memberDetails.jbtEmail ?? "");
    setEmployer(memberDetails.employer ?? "");
    setStreet1(memberDetails.street1 ?? "");
    setPlz1State(memberDetails.postalCode1 ? memberDetails.postalCode1.toString() : "");
    setPlaceOfResidence1(memberDetails.city1 ?? "");
  };

  /**
   * Handles the click on the edit button of the general information section
   * @param event MouseEvent
   */
  const handleGeneralInfoDialogOpen = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    event.stopPropagation();
    resetGeneralInfoFields();
    setGeneralInfoDialogOpen(true);
  };

  /**
   * Handles the closing of the general information dialog
   */
  const handleGeneralInfoDialogClose: VoidFunction = () => {
    resetGeneralInfoFields();
    setGeneralInfoDialogOpen(false);
  };

  /**
   * Copies the saved club information into the dialog fields.
   */
  const resetClubInfoFields = () => {
    setDepartment(memberDetails.department ?? null);
    setMentorState(memberDetails.mentor ? memberDetails.mentor : null);
    setPassiveSince(memberDetails.passiveSince ?? null);
    setAlumniSince(memberDetails.alumnusSince ?? null);
    setSeniorSince(memberDetails.seniorSince ?? null);
    setMemberSince(memberDetails.memberSince ?? null);
    setTraineeSince(memberDetails.traineeSince ?? null);
    setDepartmentError(false);
  };

  /**
   * Handles the click on the edit button of the club information section
   * @param event MouseEvent
   */
  const handleClubInfoDialogOpen = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    event.stopPropagation();
    resetClubInfoFields();
    setClubInfoDialogOpen(true);
  };

  /**
   * Handles the closing of the club information dialog
   */
  const handleClubInfoDialogClose: VoidFunction = () => {
    resetClubInfoFields();
    setClubInfoDialogOpen(false);
  };

  /**
   * Copies the saved private address into the dialog fields.
   */
  const resetPrivateAddressFields = () => {
    setStreet2(memberDetails.street2 ?? "");
    setPlz2State(memberDetails.postalCode2 ? memberDetails.postalCode2.toString() : "");
    setPlaceOfResidence2(memberDetails.city2 ?? "");
    setTelephone2(memberDetails.phone2 ? memberDetails.phone2.toString() : "");
    setEmail1State(memberDetails.email1 ?? "");
    setEmail2State(memberDetails.email2 ?? "");
  };

  /**
   * Handles the click on the edit button of the private address information section
   * @param event MouseEvent
   */
  const handlePrivateAddressInfoDialogOpen = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    event.stopPropagation();
    resetPrivateAddressFields();
    setPrivateAddressInfoDialogOpen(true);
  };

  /**
   * Handles the closing of the private address information dialog
   */
  const handlePrivateAddressInfoDialogClose: VoidFunction = () => {
    resetPrivateAddressFields();
    setPrivateAddressInfoDialogOpen(false);
  };

  /**
   * Copies the saved study information into the dialog fields.
   */
  const resetStudyInfoFields = () => {
    setUniversity(memberDetails.university ?? "");
    setCourseOfStudy(memberDetails.courseOfStudy ?? "");
    setStartOfStudy(memberDetails.studyStart ?? null);
    setEndOfStudy(memberDetails.studyEnd ?? null);
    setSpeciality(memberDetails.specializations ?? "");
  };

  /**
   * Handles the click on the edit button of the study information section
   * @param event MouseEvent
   */
  const handleStudyInfoDialogOpen = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    event.stopPropagation();
    resetStudyInfoFields();
    setStudyInfoDialogOpen(true);
  };

  /**
   * Handles the closing of the study information dialog
   */
  const handleStudyInfoDialogClose: VoidFunction = () => {
    resetStudyInfoFields();
    setStudyInfoDialogOpen(false);
  };

  /**
   * Copies the saved payment information into the dialog fields.
   */
  const resetPaymentInfoFields = () => {
    setAccountHolder(memberDetails.accountHolder ?? "");
    setIbanState(memberDetails.iban ?? "");
    setBicState(memberDetails.bic ?? "");
  };

  /**
   * Handles the click on the edit button of the payment information section
   * @param event MouseEvent
   */
  const handlePaymentInfoDialogOpen = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    event.stopPropagation();
    resetPaymentInfoFields();
    setPaymentInfoDialogOpen(true);
  };

  /**
   * Handles the closing of the payment information dialog
   */
  const handlePaymentInfoDialogClose: VoidFunction = () => {
    resetPaymentInfoFields();
    setPaymentInfoDialogOpen(false);
  };

  /**
   * Copies the saved qualification information into the dialog fields.
   */
  const resetQualificationInfoFields = () => {
    setApprenticeship(memberDetails.apprenticeship ?? "");
  };

  /**
   * Handles the click on the edit button of the qualification information section
   * @param event MouseEvent
   */
  const handleQualificationInfoDialogOpen = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    event.stopPropagation();
    resetQualificationInfoFields();
    setQualificationInfoDialogOpen(true);
  };

  /**
   * Handles the closing of the qualification information dialog
   */
  const handleQualificationInfoDialogClose: VoidFunction = () => {
    resetQualificationInfoFields();
    setQualificationInfoDialogOpen(false);
  };

  /**
   * Closes every edit dialog without copying member details back into the fields.
   * The saved values are already in local state; resetting here would restore the
   * details from before this render's refetch.
   */
  const closeDialogsAfterSave = () => {
    setGeneralInfoDialogOpen(false);
    setClubInfoDialogOpen(false);
    setPrivateAddressInfoDialogOpen(false);
    setStudyInfoDialogOpen(false);
    setQualificationInfoDialogOpen(false);
    setPaymentInfoDialogOpen(false);
  };

  /**
   * Submits the changed data
   * @param event FormEvent
   */
  const submit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const sanitizedSmartphone = sanitizeInternationalPhoneNumber(smartphone);

    if (sanitizedSmartphone && !isValidInternationalPhoneNumber(sanitizedSmartphone)) {
      showErrorMessage(getInternationalPhoneValidationMessage(smartphone));
      return;
    }

    if (!department) {
      setDepartmentError(true);
      return;
    }
    setDepartmentError(false);
    const newDepartment = {
      departmentId: department.departmentId,
      name: department.name,
      shortName: department.shortName,
    } as membersTypes.DepartmentPartialDto;
    const shouldUpdateFinance = accountHolder !== "" || ibanState !== "" || bicState !== "";
    if (shouldUpdateFinance) {
      // Check if all required fields are filled - only if finance data will be updated
      if (!accountHolder || !ibanState || !bicState) {
        showErrorMessage("Alle Felder müssen ausgefüllt sein.");
        return;
      }
      // Check if the IBAN format is valid
      if (!checkIban(ibanState)) {
        showErrorMessage("Ungültiges IBAN-Format.");
        return;
      }

      // Check if the BIC format is valid
      if (!checkBic(bicState)) {
        showErrorMessage("Ungültiges BIC-Format.");
        return;
      }
    }

    // Data which will be submitted
    const data: membersTypes.MemberDetailsDto = {
      memberId: memberDetails.memberId,
      lastname: lastname,
      firstname: name,
      gender: memberDetails.gender,
      birthday: birthday,
      mobile: sanitizedSmartphone || null,
      jbtEmail: jbtMail,
      memberStatus: memberState,
      generation: memberDetails.generation,
      internalProject: memberDetails.internalProject,
      traineeSince: traineeSince,
      memberSince: memberSince,
      alumnusSince: alumniSince,
      seniorSince: seniorSince,
      activeSince: memberDetails.activeSince,
      passiveSince: passiveSince,
      exitedSince: memberDetails.exitedSince,
      department: newDepartment,
      employer: employer,
      street1: street1,
      postalCode1: plz1State,
      city1: placeOfResidence1,
      phone1: telephone1,
      email1: email1State,
      street2: street2,
      postalCode2: plz2State,
      city2: placeOfResidence2,
      phone2: telephone2,
      email2: email2State,
      university: university,
      courseOfStudy: courseOfStudy,
      studyStart: startOfStudy,
      studyEnd: endOfStudy,
      specializations: speciality,
      apprenticeship: apprenticeship,
      commitment: commitmentState,
      canPL: memberDetails.canPL,
      canQM: memberDetails.canQM,
      lastChange: new Date(),
      drivingLicense: driversLicense ? 1 : 0,
      firstAidTraining: Boolean(firstAid),
      // Only include finance data if it's not empty
      accountHolder: accountHolder === "" ? undefined : accountHolder,
      iban: ibanState === "" ? undefined : ibanState,
      bic: bicState === "" ? undefined : bicState,
      languages: languages.map((lang) => ({ ...lang, level: Number(lang.level) })),
      itSkills: itSkills.map((itSkill) => ({ ...itSkill, level: Number(itSkill.level) })),
      mentees: menteeList,
      mentor: mentorState,
    };
    await props.updateMemberDetails(data);
    closeDialogsAfterSave();
  };

  /**
   * Toogles the career area
   */
  const toggleCareerState: VoidFunction = () => {
    setCareerOpen(!careerOpen);
  };

  /**
   * Returns the string representation of the niveau number of a language
   * @param niveau A number which represents the niveau of a language
   * @returns A string representation of the niveau
   */
  const getLanguageNiveauLabel = (niveau: number): string => {
    switch (niveau) {
      case 1: {
        return "Grundkenntnisse";
      }
      case 2: {
        return "Gute Kenntnisse";
      }
      case 3: {
        return "Fließend";
      }
      case 4: {
        return "Verhandlungssicher";
      }
      case 5: {
        return "Muttersprache";
      }
      default: {
        return "";
      }
    }
  };

  /**
   * Returns the string representation of the niveau number of an edv skill
   * @param niveau A number which represents the niveau of an edv skill
   * @returns A string representation of the niveau
   */
  const getEDVNiveauLabel = (niveau: number): string => {
    switch (niveau) {
      case 1: {
        return "Grundkenntnisse";
      }
      case 2: {
        return "Vertiefte Kenntnisse";
      }
      case 3: {
        return "Expertenwissen";
      }
      default: {
        return "";
      }
    }
  };

  /**
   * Renders the image of the member, his name and his status
   */
  const renderImage = () => {
    return (
      <Box sx={styles.imageSection}>
        <MemberImage
          base64={props.memberImage?.base64}
          mimeType={props.memberImage?.mimeType}
          defaultImage={JBTLogoBlack}
          alt="Member Image"
          size={240}
          onImageChange={props.isOwner ? saveImage : undefined}
        />
        <Box sx={styles.imageSectionText}>
          <Typography variant="h6">{`${memberDetails.firstname} ${memberDetails.lastname}`}</Typography>
          {memberDirectorPositions.map((position, index) => {
            if (new Date(position.from) <= new Date() && new Date(position.until) >= new Date()) {
              return (
                <Typography key={index}>
                  <i>{`${position.shortName}`}</i>
                </Typography>
              );
            }
            return null;
          })}
          <Typography>
            <i>{`${memberDetails.memberStatus?.name}`}</i>
          </Typography>
          {doesPermissionsHaveSomeOf(props.listOfPermissions, [1]) && (
            <Button
              variant="contained"
              onClick={() => {
                setIsEditingDirectorPosition(true);
              }}
            >
              EV Posten
            </Button>
          )}
        </Box>
      </Box>
    );
  };

  /**
   * Changes the von value of the director position to be inserted
   * @param memberID Id of the member of the director position
   * @param directorId Id of the director position
   * @param from The from value fo the director position that should be changed
   * @returns The updated director position
   */
  const setDirectorEntryFrom = (memberID: number, directorId: number, from: Dayjs | null) => {
    if (from === null) {
      return;
    }

    // Find an entry of the editedMemberDirectorPositions that matches memberID and directorId and change the from and until to the parameters given to the function
    setEditedMemberDirectorPositions((prevPositions) =>
      prevPositions.map((position) => {
        // Check if the position matches the provided memberID and directorId
        if (position.memberId === memberID && position.memberId === directorId) {
          if (from > dayjs(position.until)) {
            showErrorMessage("Das von Datum muss vor dem bis Datum liegen");
            return position;
          } else {
            // If it matches, update the from and until properties
            return {
              ...position,
              until: position.until,
              from: from.toDate(),
            };
          }
        } else {
          // If it doesn't match, return the position unchanged
          return position;
        }
      })
    );
  };

  /**
   * Changes the bis value of the director position to be inserted
   * @param memberID Id of the member of the director position
   * @param directorId Id of the director position
   * @param until The until value fo the director position that should be changed
   * @returns The updated director position
   */
  const setDirectorEntryUntil = (memberID: number, directorId: number, until: Dayjs | null) => {
    if (until === null) {
      return;
    }

    // Find an entry of the editedMemberDirectorPositions that matches memberID and directorId and change the von and bis to the parameters given to the function
    setEditedMemberDirectorPositions((prevPositions) =>
      prevPositions.map((position) => {
        // Check if the position matches the provided memberID and directorId
        if (position.memberId === memberID && position.directorId === directorId) {
          if (dayjs(position.from) > until) {
            showErrorMessage("Das von Datum muss vor dem bis Datum liegen");
            return position;
          } else {
            // If it matches, update the von and bis properties
            return {
              ...position,
              until: until.toDate(),
              from: position.from,
            };
          }
        } else {
          // If it doesn't match, return the position unchanged
          return position;
        }
      })
    );
  };

  /**
   * Removes an entry to the list of director posistion
   * @param memberID Id of the member of the director position
   * @param directorId Id of the director position
   * @param del The boolean value that specifies whether the entry should be deleted
   */
  const setDirectorEntryDelete = (memberID: number, directorId: number, del: boolean) => {
    // Find an entry of the editedMemberDirectorPositions that matches memberID and directorId and change the von and bis to the parameters given to the function
    setEditedMemberDirectorPositions((prevPositions) =>
      prevPositions.map((position) => {
        // Check if the position matches the provided memberID and directorId
        if (position.memberId === memberID && position.directorId === directorId) {
          // If it matches, update the von and bis properties
          return {
            ...position,
            delete: del,
          };
        } else {
          // If it doesn't match, return the position unchanged
          return position;
        }
      })
    );
  };

  /**
   * Adds a new entry to the list of director position
   * @param memberId  Id of the member of the director position
   * @param directorId Id of the director position
   * @param from The from value of the director position
   * @param until The until value of the director position
   */
  const addDirectorEntry = (memberId: number, directorId: number, from: Dayjs | null, until: Dayjs | null) => {
    if (until === null || from === null) {
      return;
    }

    let posExists = false;
    memberDirectorPositions.forEach((position) => {
      if (position.memberId === memberId && position.directorId === directorId) {
        posExists = true;
        return;
      }
    });

    if (posExists) {
      showErrorMessage("Ein Mitglied kann jeden EV Posten nur einmal belegen.");
      return;
    }

    let shortName = "";
    directorPositions.forEach((position) => {
      if (position.directorId === directorId) {
        shortName = position.shortName;
        return;
      }
    });

    if (shortName === "") {
      return;
    }

    const newEntry: EditedMemberDirectorPositions = {
      directorId,
      memberId,
      shortName,
      from: from.toDate(),
      until: until.toDate(),
      delete: false,
    };

    setEditedMemberDirectorPositions((prevState) => [...prevState, newEntry]);
  };

  /**
   * Loops through all entries of director position and checks which need to be updated, deleted or added
   * and adds them to the database
   */
  const saveDirectorPosition = async () => {
    let rowsChanged = 0;

    // Use map to create an array of promises
    const promises = editedMemberDirectorPositions.map(async (editedPosition) => {
      let foundValue = false;
      let update = false;
      memberDirectorPositions.forEach((oldPosition) => {
        if (editedPosition.directorId === oldPosition.directorId && editedPosition.memberId === oldPosition.memberId) {
          foundValue = true;
          if (editedPosition.until !== oldPosition.until || editedPosition.from !== oldPosition.from) {
            update = true;
            return;
          }
        }
      });

      if (!foundValue) {
        if (!editedPosition.delete) {
          await addDirectorPosition(editedPosition.directorId, editedPosition.from, editedPosition.until);
        }
        rowsChanged += 1;
      } else if (editedPosition.delete) {
        await deleteDirectorPosition(editedPosition.directorId);
        rowsChanged += 1;
      } else if (update) {
        await changeDirectorPosition(editedPosition.directorId, editedPosition.from, editedPosition.until);
        rowsChanged += 1;
      }
    });

    // Wait for all promises to resolve
    await Promise.all(promises);

    if (rowsChanged > 0) {
      showSuccessMessage("Änderungen erfolgreich gespeichert.");
    } else {
      showErrorMessage("Es wurden keine Änderungen vorgenommen.");
    }
  };

  // Closes the edit director position modal without saving
  const cancelEditDirectorPosition = () => {
    setIsEditingDirectorPosition(false);
    setEditedMemberDirectorPositions(props.memberDirectorPositions.map((position) => ({ ...position, delete: false })));
  };

  /**
   * Renders the modal that displays all director positions that the member has and lets the user make changes
   */
  const renderDirectorPosition = (): React.ReactNode => {
    return (
      <Modal
        open={isEditingDirectorPosition}
        onClose={() => cancelEditDirectorPosition()}
        aria-labelledby="EV Posten zuweisen"
        aria-describedby="Zuweisung eines EV Posten"
      >
        <Paper sx={styles.modalPaper}>
          <Grid container>
            <Grid item xs={12}>
              <Typography variant="h5" sx={styles.paperHeaderText}>
                Vorstandsämter bearbeiten
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="h6" sx={styles.paperHeaderText}>
                Jedes Mitglied kann ein bestimmtes Vorstandsamt nur einmal besetzen.
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <TableContainer component={Paper} sx={styles.tableContainer}>
                <Table stickyHeader aria-label="sticky table">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={styles.tableHeadCell}>EV Amt von</TableCell>
                      <TableCell sx={styles.tableHeadCell}>EV Amt bis</TableCell>
                      <TableCell sx={styles.tableHeadCell}>EV Amt</TableCell>
                      <TableCell sx={styles.tableHeadCell}>Löschen</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {editedMemberDirectorPositions.map((position, index) => {
                      return (
                        <TableRow hover key={index}>
                          <TableCell
                            sx={index === editedMemberDirectorPositions.length - 1 ? styles.tableSecondLastRow : null}
                          >
                            <DatePicker
                              label="von"
                              value={dayjs(position.from)}
                              onChange={(newValue) =>
                                setDirectorEntryFrom(position.memberId, position.directorId, newValue)
                              }
                            />
                          </TableCell>
                          <TableCell
                            sx={index === editedMemberDirectorPositions.length - 1 ? styles.tableSecondLastRow : null}
                          >
                            <DatePicker
                              label="bis"
                              value={dayjs(position.until)}
                              onChange={(newValue) =>
                                setDirectorEntryUntil(position.memberId, position.directorId, newValue)
                              }
                            />
                          </TableCell>
                          <TableCell
                            sx={index === editedMemberDirectorPositions.length - 1 ? styles.tableSecondLastRow : null}
                          >
                            {position.shortName}
                          </TableCell>
                          <TableCell
                            sx={index === editedMemberDirectorPositions.length - 1 ? styles.tableSecondLastRow : null}
                          >
                            <Checkbox
                              color="primary"
                              checked={position.delete}
                              onChange={(event) =>
                                setDirectorEntryDelete(position.memberId, position.directorId, event.target.checked)
                              }
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    <TableRow hover key={999}>
                      <TableCell>
                        <DatePicker label="von" value={fromValue} onChange={(newValue) => setFromValue(newValue)} />
                      </TableCell>
                      <TableCell>
                        <DatePicker label="bis" value={untilValue} onChange={(newValue) => setUntilValue(newValue)} />
                      </TableCell>
                      <TableCell>
                        <Select
                          label="EV Position"
                          defaultValue={-1}
                          value={directorPosition}
                          onChange={(event) => setDirectorPosition(event.target.value as number)}
                        >
                          {directorPositions.map((position, index) => {
                            return (
                              <MenuItem key={index} value={position.directorId}>
                                {position.shortName}
                              </MenuItem>
                            );
                          })}
                        </Select>
                      </TableCell>
                      <TableCell>
                        <IconButton
                          aria-label="add"
                          disabled={directorPosition < 0 || !fromValue || !untilValue}
                          onClick={() =>
                            addDirectorEntry(memberDetails.memberId, directorPosition, fromValue, untilValue)
                          }
                        >
                          <Add />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
            <Button
              sx={styles.vorstandSubmitButton}
              variant="contained"
              color="primary"
              onClick={() => saveDirectorPosition()}
            >
              Änderungen speichern
            </Button>
            <Button
              sx={styles.vorstandCancelButton}
              variant="contained"
              color="secondary"
              onClick={() => cancelEditDirectorPosition()}
            >
              Abbrechen
            </Button>
          </Grid>
        </Paper>
      </Modal>
    );
  };

  /**
   * Renders the category for general information
   */
  const renderGeneralInformation = (): React.ReactNode => {
    return (
      <Grid item xs={12} sm={12}>
        <InfoCard
          title={"Allgemeine Angaben"}
          isExpandable={true}
          defaultExpanded={true}
          /* When the user is owner or has the permission to
            manage all members they can edit this section */
          isEditable={props.isOwner || doesPermissionsHaveSomeOf(props.listOfPermissions, [1])}
          handleEdit={handleGeneralInfoDialogOpen}
        >
          <Box sx={styles.category}>
            <Box sx={styles.categoryItem}>
              <Typography sx={styles.categoryLine}>Geburtsdatum:&nbsp;&nbsp;</Typography>
              <Typography sx={styles.categoryLine}>{transformToGermanDate(memberDetails.birthday)}</Typography>
            </Box>
            <Box sx={styles.categoryItem}>
              <Typography sx={styles.categoryLine}>Handy:&nbsp;&nbsp;</Typography>
              <Typography sx={styles.categoryLine}>{formatPhoneNumber(memberDetails.mobile)}</Typography>
            </Box>
            <Box sx={styles.jbtEmailItem}>
              <Typography sx={{ ...styles.categoryLine, whiteSpace: "nowrap" }}>JBT-E-Mail:&nbsp;&nbsp;</Typography>
              <Typography sx={{ ...styles.categoryLine, whiteSpace: "nowrap" }}>{memberDetails.jbtEmail}</Typography>
            </Box>
            <Box sx={styles.categoryItem}>
              <Typography sx={styles.categoryLine}>Straße/Hausnummer:&nbsp;&nbsp;</Typography>
              <Typography sx={styles.categoryLine}>{memberDetails.street1}</Typography>
            </Box>
            <Box sx={styles.categoryItem}>
              <Typography sx={styles.categoryLine}>PLZ/Ort:&nbsp;&nbsp;</Typography>
              <Typography sx={styles.categoryLine}>
                {[memberDetails.postalCode1, memberDetails.city1].filter(Boolean).join(" ")}
              </Typography>
            </Box>
            <Box sx={styles.categoryItem}>
              <Typography sx={styles.categoryLine}>Arbeitgeber:&nbsp;&nbsp;</Typography>
              <Typography sx={styles.categoryLine}>{memberDetails.employer}</Typography>
            </Box>
          </Box>
        </InfoCard>
      </Grid>
    );
  };

  /**
   * Renders the different items for the career section
   */
  const renderCareerItems = (): React.ReactNode => {
    return (
      <div>
        {memberDetails.passiveSince ? (
          <div>
            <Typography sx={styles.categoryItem}>
              <strong>{"Passives Mitglied"}</strong>
            </Typography>
            <Typography>{`Seit ${memberDetails.passiveSince.toString()}`}</Typography>
          </div>
        ) : null}
        {memberDetails.alumnusSince ? (
          <div>
            <Typography sx={styles.categoryItem}>
              <strong>{"Alumna*Alumnus"}</strong>
            </Typography>
            <Typography>{`Seit ${memberDetails.alumnusSince.toString()}`}</Typography>
          </div>
        ) : null}
        {memberDetails.seniorSince ? (
          <div>
            <Typography sx={styles.categoryItem}>
              <strong>{"Senior"}</strong>
            </Typography>
            <Typography>{`Seit ${memberDetails.seniorSince.toString()}`}</Typography>
          </div>
        ) : null}
        {memberDetails.memberSince ? (
          <div>
            <Typography sx={styles.categoryItem}>
              <strong>{"Aktives Mitglied"}</strong>
            </Typography>
            <Typography>{`Seit ${memberDetails.memberSince.toString()}`}</Typography>
          </div>
        ) : null}
        {memberDetails.traineeSince ? (
          <div>
            <Typography sx={styles.categoryItem}>
              <strong>{"Trainee"}</strong>
            </Typography>
            <Typography>{`Seit ${memberDetails.traineeSince.toString()}`}</Typography>
          </div>
        ) : null}
        <hr />
      </div>
    );
  };

  /**
   * Renders the category for club information
   */
  const renderClubInformation = (): React.ReactNode => {
    return (
      <Grid item xs={12} sm={12}>
        <InfoCard
          title={"Verein"}
          isExpandable={true}
          defaultExpanded={true}
          /* When the has the permission to
            manage all members they can edit this section */
          isEditable={doesPermissionsHaveSomeOf(props.listOfPermissions, [1])}
          handleEdit={handleClubInfoDialogOpen}
        >
          <Box sx={styles.category}>
            <Box sx={styles.category}>
              <Box sx={styles.categoryItem}>
                <Typography sx={styles.categoryLine}>Ressort:&nbsp;&nbsp;</Typography>
                <Typography sx={styles.categoryLine}>{memberDetails.department?.name}</Typography>
              </Box>
              <Box sx={styles.categoryItem}>
                <Typography sx={styles.categoryLine}>Mentor:&nbsp;&nbsp;</Typography>
                {memberDetails.mentor && memberDetails.mentor.firstname && memberDetails.mentor.lastname ? (
                  <Link
                    to={`/gesamtuebersicht/${memberDetails.mentor.memberId}`}
                    style={{
                      paddingTop: "12.5px",
                      paddingBottom: "11.5px",
                      textAlign: "right",
                      textDecoration: "none",
                      color: theme.palette.text.secondary,
                    }}
                  >
                    {`${memberDetails.mentor.firstname} ${memberDetails.mentor.lastname}`}
                  </Link>
                ) : (
                  ""
                )}
              </Box>
              <Box sx={styles.categoryItem}>
                <Typography sx={styles.categoryLine}>Mentees:</Typography>
                <Box sx={styles.categoryItemList}>
                  {menteeList.map((mentee, index) => {
                    return (
                      <Typography
                        sx={styles.categoryLine}
                        key={index}
                      >{`${mentee.firstname} ${mentee.lastname}`}</Typography>
                    );
                  })}
                </Box>
              </Box>
            </Box>
            <Box>
              <Box>
                <>
                  <Box sx={styles.subCategoryHeader}>
                    <Typography>Werdegang</Typography>
                    <IconButton aria-label="expand career" onClick={toggleCareerState} size="large">
                      {careerOpen ? <ExpandLess fontSize="inherit" /> : <ExpandMore fontSize="inherit" />}
                    </IconButton>
                  </Box>
                  <hr />
                  {careerOpen ? renderCareerItems() : null}
                </>
              </Box>
            </Box>
          </Box>
        </InfoCard>
      </Grid>
    );
  };

  /**
   * Renders the category for private address information
   */
  const renderPrivateAddressInformation = () => {
    return (
      <Grid item xs={12} sm={12}>
        <InfoCard
          title={"Heimatanschrift"}
          isExpandable={true}
          defaultExpanded={true}
          /* When the user is owner they can edit this section */
          isEditable={props.isOwner}
          handleEdit={handlePrivateAddressInfoDialogOpen}
        >
          <Box sx={styles.category}>
            <Box sx={styles.categoryItem}>
              <Typography sx={styles.categoryLine}>Straße:&nbsp;&nbsp;</Typography>
              <Typography sx={styles.categoryLine}>{memberDetails.street2}</Typography>
            </Box>
            <Box sx={styles.categoryItem}>
              <Typography sx={styles.categoryLine}>PLZ:&nbsp;&nbsp;</Typography>
              <Typography sx={styles.categoryLine}>{memberDetails.postalCode2}</Typography>
            </Box>
            <Box sx={styles.categoryItem}>
              <Typography sx={styles.categoryLine}>Ort:&nbsp;&nbsp;</Typography>
              <Typography sx={styles.categoryLine}>{memberDetails.city2}</Typography>
            </Box>
            <Box sx={styles.categoryItem}>
              <Typography sx={styles.categoryLine}>Telefon:&nbsp;&nbsp;</Typography>
              <Typography sx={styles.categoryLine}>{memberDetails.phone2}</Typography>
            </Box>
            <Box sx={styles.categoryItem}>
              <Typography sx={styles.categoryLine}>E-Mail privat:&nbsp;&nbsp;</Typography>
              <Typography sx={styles.categoryLine}>{memberDetails.email2}</Typography>
            </Box>
            <Box sx={styles.categoryItem}>
              <Typography sx={styles.categoryLine}>E-Mail geschäftlich:&nbsp;&nbsp;</Typography>
              <Typography sx={styles.categoryLine}>{memberDetails.email1}</Typography>
            </Box>
          </Box>
        </InfoCard>
      </Grid>
    );
  };

  /**
   * Renders the category for club information
   */
  const renderStudyInformation = (): React.ReactNode => {
    return (
      <Grid item xs={12} sm={12}>
        <InfoCard
          title={"Studium"}
          isExpandable={true}
          defaultExpanded={true}
          /* When the user is owner they can edit this section */
          isEditable={props.isOwner}
          handleEdit={handleStudyInfoDialogOpen}
        >
          <Box sx={styles.category}>
            <Box sx={styles.categoryItem}>
              <Typography sx={styles.categoryLine}>Hochschule:&nbsp;&nbsp;</Typography>
              <Typography sx={styles.categoryLine}>{memberDetails.university}</Typography>
            </Box>
            <Box sx={styles.categoryItem}>
              <Typography sx={styles.categoryLine}>Studiengang:&nbsp;&nbsp;</Typography>
              <Typography sx={styles.categoryLine}>{memberDetails.courseOfStudy}</Typography>
            </Box>
            <Box sx={styles.categoryItem}>
              <Typography sx={styles.categoryLine}>Studienbeginn:&nbsp;&nbsp;</Typography>
              <Typography sx={styles.categoryLine}>{memberDetails.studyStart?.toString()}</Typography>
            </Box>
            <Box sx={styles.categoryItem}>
              <Typography sx={styles.categoryLine}>Studienende:&nbsp;&nbsp;</Typography>
              <Typography sx={styles.categoryLine}>{memberDetails.studyEnd?.toString()}</Typography>
            </Box>
            <Box sx={styles.categoryItem}>
              <Typography sx={styles.categoryLine}>Vertiefungen:&nbsp;&nbsp;</Typography>
              <Typography sx={styles.categoryLine}>{memberDetails.specializations}</Typography>
            </Box>
          </Box>
        </InfoCard>
      </Grid>
    );
  };

  /**
   * Renders the category for payment informations
   */
  const renderPaymentInformation = (): React.ReactNode => {
    if (props.isOwner || doesPermissionsHaveSomeOf(props.listOfPermissions, [6])) {
      return (
        <Grid item xs={12} sm={12}>
          <InfoCard
            title={"Zahlungsinformationen"}
            isExpandable={true}
            defaultExpanded={false}
            /* When the user is owner they can edit this section */
            isEditable={props.isOwner}
            handleEdit={handlePaymentInfoDialogOpen}
          >
            <Box sx={styles.category}>
              <Box sx={styles.categoryItem}>
                <Typography sx={styles.categoryLine}>Kontoinhaber:&nbsp;&nbsp;</Typography>
                <Typography sx={styles.categoryLine}>{memberDetails.accountHolder}</Typography>
              </Box>
              <Box sx={styles.categoryItem}>
                <Typography sx={styles.categoryLine}>IBAN:&nbsp;&nbsp;</Typography>
                <Typography sx={styles.categoryLine}>{memberDetails.iban}</Typography>
              </Box>
              <Box sx={styles.categoryItem}>
                <Typography sx={styles.categoryLine}>BIC:&nbsp;&nbsp;</Typography>
                <Typography sx={styles.categoryLine}>{memberDetails.bic}</Typography>
              </Box>
            </Box>
          </InfoCard>
        </Grid>
      );
    } else {
      return null;
    }
  };

  /**
   * Renders the category for qualification information
   */
  const renderQualificationInformation = (): React.ReactNode => {
    return (
      <Grid item xs={12} sm={12}>
        <InfoCard
          title={"Qualifikationen"}
          isExpandable={true}
          defaultExpanded={true}
          /* When the user is owner they can edit this section */
          isEditable={props.isOwner}
          handleEdit={handleQualificationInfoDialogOpen}
        >
          <Box sx={styles.category}>
            <Box sx={styles.categoryItem}>
              <Typography sx={styles.categoryLine}>Ausbildung:&nbsp;&nbsp;</Typography>
              <Typography sx={styles.categoryLine}>{memberDetails.apprenticeship}</Typography>
            </Box>
            <Box sx={styles.categoryItem}>
              <Typography sx={styles.categoryLine}>Sprachen:</Typography>
              <Box sx={styles.categoryItemList}>
                {(memberDetails?.languages || []).map((language) => {
                  return (
                    <Typography sx={styles.categoryLine}>
                      {`${language.value}: ${getLanguageNiveauLabel(language.level)}`}
                    </Typography>
                  );
                })}
              </Box>
            </Box>
            <Box sx={styles.categoryItem}>
              <Typography sx={styles.categoryLine}>EDV-Kenntnisse:</Typography>
              <Box sx={styles.categoryItemList}>
                {(memberDetails?.itSkills || []).map((it) => {
                  return (
                    <Typography sx={styles.categoryLine}>{`${it.value}: ${getEDVNiveauLabel(it.level)}`}</Typography>
                  );
                })}
              </Box>
            </Box>
          </Box>
        </InfoCard>
      </Grid>
    );
  };

  /**
   * Renders the the list of projects
   */
  const renderProjectList = (): React.ReactNode => {
    return (
      <Grid item xs={12} sm={12}>
        <InfoCard title={"Projekte (# Anzahl)"} isExpandable={true} defaultExpanded={true} isEditable={false}>
          <Box sx={styles.category}>
            <div>
              <Typography sx={styles.categoryItem}>
                <strong>{"Name des Projekts"}</strong>
              </Typography>
              <Typography sx={styles.categoryItem}>{`Projektzeitraum: von ${""} bis ${""}`}</Typography>
              <Typography sx={styles.categoryItem}>{`Rolle: ${""}`}</Typography>
              <Typography sx={styles.categoryItem}>{`Geleistete BT: ${""}`}</Typography>
            </div>
            <hr />
          </Box>
        </InfoCard>
      </Grid>
    );
  };

  /**
   * Renders the the list of workshops
   */
  const renderWorkshopList = (): React.ReactNode => {
    return (
      <Grid item xs={12} sm={12}>
        <InfoCard title={"Workshops (# Anzahl)"} isExpandable={true} defaultExpanded={true} isEditable={false}>
          <Box sx={styles.category}>
            <Box sx={styles.workshopItem}>
              <Typography>{`${"DATUM"}`}</Typography>
              <Typography>{`${"ART DES WS"}`}</Typography>
              <Typography>{`${"NAME"}`}</Typography>
              <Typography>{`${"GEHALTEN"}`}</Typography>
            </Box>
          </Box>
        </InfoCard>
      </Grid>
    );
  };

  /**
   * Renders the dialog for changing the general informations
   */
  const renderGeneralInformationDialog = (): React.ReactNode => {
    return (
      <Dialog
        open={generalInfoDialogOpen}
        onClose={handleGeneralInfoDialogClose}
        fullWidth
        maxWidth="lg"
        keepMounted
        aria-labelledby="general-dialog-title"
      >
        <DialogTitle id="general-dialog-title">Allgemeine Informationen</DialogTitle>
        <DialogContent sx={styles.dialogContent}>
          <form autoComplete="off" onSubmit={submit}>
            <Grid container spacing={2} sx={styles.dialogFormGrid}>
              <Grid item xs={12} sm={12} md={12} lg={12}>
                <DatePicker
                  label="Geburtstag"
                  value={birthday ? dayjs(birthday) : null}
                  onChange={(newValue) => {
                    setBirthday(newValue ? newValue.toDate() : null);
                  }}
                  disabled
                  slotProps={{
                    textField: {
                      sx: styles.fullWidth,
                      required: true,
                      color: "primary",
                      variant: "outlined",
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={12} md={12} lg={12}>
                <TextField
                  sx={styles.fullWidth}
                  color="primary"
                  disabled={!props.isOwner}
                  id="smartphone-field"
                  label="Handynummer"
                  variant="outlined"
                  value={smartphone}
                  onChange={(event) => {
                    setSmartphone(sanitizeSmartphoneInput(event.target.value));
                  }}
                  inputProps={{ inputMode: "tel", pattern: "\\+[1-9][0-9]{4,14}", maxLength: 16 }}
                />
              </Grid>
              <Grid item xs={12} sm={12} md={12} lg={12}>
                <TextField
                  sx={styles.fullWidth}
                  color="primary"
                  required
                  disabled={!doesPermissionsHaveSomeOf(props.listOfPermissions, [1])}
                  id="jbt-email-field"
                  label="JBT-E-Mail"
                  variant="outlined"
                  value={jbtMail}
                  onChange={(event) => {
                    setJbtMail(event.target.value);
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={12} md={12} lg={12}>
                <TextField
                  sx={styles.fullWidth}
                  color="primary"
                  disabled={!props.isOwner}
                  id="street-field"
                  label="Straße/Hausnummer"
                  variant="outlined"
                  value={street1}
                  onChange={(event) => {
                    setStreet1(event.target.value);
                  }}
                />
              </Grid>
              <Grid item xs={6} sm={6} md={6} lg={6}>
                <TextField
                  sx={styles.fullWidth}
                  color="primary"
                  disabled={!props.isOwner}
                  id="plz-field"
                  label="PLZ"
                  variant="outlined"
                  value={plz1State}
                  onChange={(event) => {
                    setPlz1State(event.target.value);
                  }}
                />
              </Grid>
              <Grid item xs={6} sm={6} md={6} lg={6}>
                <TextField
                  sx={styles.fullWidth}
                  color="primary"
                  disabled={!props.isOwner}
                  id="place-of-residence-field"
                  label="Ort"
                  variant="outlined"
                  value={placeOfResidence1}
                  onChange={(event) => {
                    setPlaceOfResidence1(event.target.value);
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={12} md={12} lg={12}>
                <TextField
                  sx={styles.fullWidth}
                  color="primary"
                  disabled={!props.isOwner}
                  id="employer-field"
                  label="Arbeitgeber"
                  variant="outlined"
                  value={employer}
                  onChange={(event) => {
                    setEmployer(event.target.value);
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={12} md={12} lg={12}>
                <hr />
              </Grid>
              <Grid item xs={12} sm={12} md={12} lg={12} sx={styles.submitContainer}>
                <Button sx={styles.cancelButton} variant="contained" onClick={handleGeneralInfoDialogClose}>
                  Abbrechen
                </Button>
                <Button sx={styles.submitButton} variant="contained" color="primary" type="submit">
                  Änderungen speichern
                </Button>
              </Grid>
            </Grid>
          </form>
        </DialogContent>
      </Dialog>
    );
  };

  /**
   * Renders the dialog for changing the club informations
   */
  const renderClubInformationDialog = (): React.ReactNode => {
    return (
      <Dialog
        open={clubInfoDialogOpen}
        onClose={handleClubInfoDialogClose}
        fullWidth
        maxWidth="lg"
        keepMounted
        aria-labelledby="club-dialog-title"
      >
        <DialogTitle id="club-dialog-title">Verein</DialogTitle>
        <DialogContent sx={styles.dialogContent}>
          <form autoComplete="off" onSubmit={submit}>
            <Grid container spacing={2} sx={styles.dialogFormGrid}>
              <Grid item xs={12} sm={12} md={12} lg={12}>
                <Autocomplete
                  id="ressort-select"
                  options={departments}
                  value={department}
                  isOptionEqualToValue={(option, value) => option.departmentId === value.departmentId}
                  onChange={(_, newDepartment) => {
                    setDepartment(newDepartment);
                  }}
                  getOptionLabel={(dep) => dep?.name ?? ""}
                  sx={styles.fullWidth}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Ressort"
                      variant="outlined"
                      required
                      error={departmentError}
                      helperText={departmentError ? "Bitte ein Ressort auswählen." : ""}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={12} md={12} lg={12}>
                <Autocomplete
                  id="mentor-select"
                  options={filteredMembers}
                  disabled={!doesPermissionsHaveSomeOf(props.listOfPermissions, [1])}
                  value={mentorState}
                  isOptionEqualToValue={(option, value) => option.memberId === value.memberId}
                  onChange={(_, newMentor) => {
                    if (newMentor === null) {
                      setMentorState(null);
                    } else {
                      setMentorState({
                        memberId: newMentor.memberId,
                        firstname: newMentor.firstname,
                        lastname: newMentor.lastname,
                      });
                    }
                  }}
                  getOptionLabel={(member) => `${member.firstname} ${member.lastname}`}
                  sx={styles.fullWidth}
                  renderInput={(params) => <TextField {...params} label="Mentor" variant="outlined" />}
                />
              </Grid>
              <Grid item xs={12} sm={12} md={12} lg={12}>
                <DatePicker
                  label="Passives Mitglied seit"
                  value={passiveSince ? dayjs(passiveSince) : null}
                  onChange={(newValue) => {
                    setPassiveSince(newValue ? newValue.toDate() : null);
                  }}
                  disabled={!doesPermissionsHaveSomeOf(props.listOfPermissions, [1])}
                  slotProps={{
                    textField: {
                      sx: styles.fullWidth,
                      color: "primary",
                      variant: "outlined",
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={12} md={12} lg={12}>
                <DatePicker
                  label="Alumna*Alumnus seit"
                  value={alumniSince ? dayjs(alumniSince) : null}
                  onChange={(newValue) => {
                    setAlumniSince(newValue ? newValue.toDate() : null);
                  }}
                  disabled={!doesPermissionsHaveSomeOf(props.listOfPermissions, [1])}
                  slotProps={{
                    textField: {
                      sx: styles.fullWidth,
                      color: "primary",
                      variant: "outlined",
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={12} md={12} lg={12}>
                <DatePicker
                  label="Senior seit"
                  value={seniorSince ? dayjs(seniorSince) : null}
                  onChange={(newValue) => {
                    setSeniorSince(newValue ? newValue.toDate() : null);
                  }}
                  disabled={!doesPermissionsHaveSomeOf(props.listOfPermissions, [1])}
                  slotProps={{
                    textField: {
                      sx: styles.fullWidth,
                      color: "primary",
                      variant: "outlined",
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={12} md={12} lg={12}>
                <DatePicker
                  label="Aktives Mitglied seit"
                  value={memberSince ? dayjs(memberSince) : null}
                  onChange={(newValue) => {
                    setMemberSince(newValue ? newValue.toDate() : null);
                  }}
                  disabled={!doesPermissionsHaveSomeOf(props.listOfPermissions, [1])}
                  slotProps={{
                    textField: {
                      sx: styles.fullWidth,
                      color: "primary",
                      variant: "outlined",
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={12} md={12} lg={12}>
                <DatePicker
                  label="Trainee seit"
                  value={traineeSince ? dayjs(traineeSince) : null}
                  onChange={(newValue) => {
                    setTraineeSince(newValue ? newValue.toDate() : null);
                  }}
                  disabled={!doesPermissionsHaveSomeOf(props.listOfPermissions, [1])}
                  slotProps={{
                    textField: {
                      sx: styles.fullWidth,
                      color: "primary",
                      variant: "outlined",
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={12} md={12} lg={12}>
                <hr />
              </Grid>
              <Grid item xs={12} sm={12} md={12} lg={12} sx={styles.submitContainer}>
                <Button sx={styles.cancelButton} variant="contained" onClick={handleClubInfoDialogClose}>
                  Abbrechen
                </Button>
                <Button sx={styles.submitButton} variant="contained" color="primary" type="submit">
                  Änderungen speichern
                </Button>
              </Grid>
            </Grid>
          </form>
        </DialogContent>
      </Dialog>
    );
  };

  /**
   * Renders the dialog for changing the study informations
   */
  const renderPrivateAddressInformationDialog = (): React.ReactNode => {
    return (
      <Dialog
        open={privateAddressInfoDialogOpen}
        onClose={handlePrivateAddressInfoDialogClose}
        fullWidth
        maxWidth="lg"
        keepMounted
        aria-labelledby="privateaddress-dialog-title"
      >
        <DialogTitle id="privateaddress-dialog-title">Heimatanschrift</DialogTitle>
        <DialogContent sx={styles.dialogContent}>
          <form autoComplete="off" onSubmit={submit}>
            <Grid container spacing={2} sx={styles.dialogFormGrid}>
              <Grid item xs={12} sm={12} md={12} lg={12}>
                <TextField
                  sx={styles.fullWidth}
                  color="primary"
                  disabled={!props.isOwner}
                  id="street2-field"
                  label="Straße"
                  variant="outlined"
                  value={street2}
                  onChange={(event) => {
                    setStreet2(event.target.value);
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={12} md={12} lg={12}>
                <TextField
                  sx={styles.fullWidth}
                  color="primary"
                  disabled={!props.isOwner}
                  id="postalCode2-field"
                  label="PLZ"
                  variant="outlined"
                  value={plz2State}
                  onChange={(event) => {
                    setPlz2State(event.target.value);
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={12} md={12} lg={12}>
                <TextField
                  sx={styles.fullWidth}
                  color="primary"
                  disabled={!props.isOwner}
                  id="city2-field"
                  label="Stadt"
                  variant="outlined"
                  value={placeOfResidence2}
                  onChange={(event) => {
                    setPlaceOfResidence2(event.target.value);
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={12} md={12} lg={12}>
                <TextField
                  sx={styles.fullWidth}
                  color="primary"
                  disabled={!props.isOwner}
                  id="phone2-field"
                  label="Telefon Privat"
                  variant="outlined"
                  value={telephone2}
                  onChange={(event) => {
                    setTelephone2(event.target.value);
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={12} md={12} lg={12}>
                <TextField
                  sx={styles.fullWidth}
                  color="primary"
                  disabled={!props.isOwner}
                  id="businessMail-field"
                  label="E-Mail geschäftlich"
                  variant="outlined"
                  value={email1State}
                  onChange={(event) => {
                    setEmail1State(event.target.value);
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={12} md={12} lg={12}>
                <TextField
                  sx={styles.fullWidth}
                  color="primary"
                  disabled={!props.isOwner}
                  id="privateMail-field"
                  label="E-Mail privat"
                  variant="outlined"
                  value={email2State}
                  onChange={(event) => {
                    setEmail2State(event.target.value);
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={12} md={12} lg={12}>
                <hr />
              </Grid>
              <Grid item xs={12} sm={12} md={12} lg={12} sx={styles.submitContainer}>
                <Button sx={styles.cancelButton} variant="contained" onClick={handlePrivateAddressInfoDialogClose}>
                  Abbrechen
                </Button>
                <Button sx={styles.submitButton} variant="contained" color="primary" type="submit">
                  Änderungen speichern
                </Button>
              </Grid>
            </Grid>
          </form>
        </DialogContent>
      </Dialog>
    );
  };

  /**
   * Renders the dialog for changing the study informations
   */
  const renderStudyInformationDialog = (): React.ReactNode => {
    return (
      <Dialog
        open={studyInfoDialogOpen}
        onClose={handleStudyInfoDialogClose}
        fullWidth
        maxWidth="lg"
        keepMounted
        aria-labelledby="study-dialog-title"
      >
        <DialogTitle id="study-dialog-title">Studium</DialogTitle>
        <DialogContent sx={styles.dialogContent}>
          <form autoComplete="off" onSubmit={submit}>
            <Grid container spacing={2} sx={styles.dialogFormGrid}>
              <Grid item xs={12} sm={12} md={12} lg={12}>
                <TextField
                  sx={styles.fullWidth}
                  color="primary"
                  disabled={!props.isOwner}
                  id="uni-field"
                  label="Hochschule"
                  variant="outlined"
                  value={university}
                  onChange={(event) => {
                    setUniversity(event.target.value);
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={12} md={12} lg={12}>
                <TextField
                  sx={styles.fullWidth}
                  color="primary"
                  disabled={!props.isOwner}
                  id="course-of-study-field"
                  label="Studiengang"
                  variant="outlined"
                  value={courseOfStudy}
                  onChange={(event) => {
                    setCourseOfStudy(event.target.value);
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={12} md={12} lg={12}>
                <DatePicker
                  label="Studienbeginn"
                  value={startOfStudy ? dayjs(startOfStudy) : null}
                  onChange={(newValue) => {
                    setStartOfStudy(newValue ? newValue.toDate() : null);
                  }}
                  disabled={!props.isOwner}
                  slotProps={{
                    textField: {
                      sx: styles.fullWidth,
                      color: "primary",
                      variant: "outlined",
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={12} md={12} lg={12}>
                <DatePicker
                  label="Studienende"
                  value={endOfStudy ? dayjs(endOfStudy) : null}
                  onChange={(newValue) => {
                    setEndOfStudy(newValue ? newValue.toDate() : null);
                  }}
                  disabled={!props.isOwner}
                  slotProps={{
                    textField: {
                      sx: styles.fullWidth,
                      color: "primary",
                      variant: "outlined",
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={12} md={12} lg={12}>
                <TextField
                  sx={styles.fullWidth}
                  color="primary"
                  disabled={!props.isOwner}
                  id="speciality-field"
                  label="Vertiefungen"
                  variant="outlined"
                  value={speciality}
                  onChange={(event) => {
                    setSpeciality(event.target.value);
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={12} md={12} lg={12}>
                <hr />
              </Grid>
              <Grid item xs={12} sm={12} md={12} lg={12} sx={styles.submitContainer}>
                <Button sx={styles.cancelButton} variant="contained" onClick={handleStudyInfoDialogClose}>
                  Abbrechen
                </Button>
                <Button sx={styles.submitButton} variant="contained" color="primary" type="submit">
                  Änderungen speichern
                </Button>
              </Grid>
            </Grid>
          </form>
        </DialogContent>
      </Dialog>
    );
  };

  /**
   * Renders the dialog for changing the qualification informations
   */
  const renderQualificationInformationDialog = (): React.ReactNode => {
    return (
      <Dialog
        open={qualificationInfoDialogOpen}
        onClose={handleQualificationInfoDialogClose}
        fullWidth
        maxWidth="lg"
        keepMounted
        aria-labelledby="qualification-dialog-title"
      >
        <DialogTitle id="qualification-dialog-title">Qualifikationen</DialogTitle>
        <DialogContent sx={styles.dialogContent}>
          <form autoComplete="off" onSubmit={submit}>
            <Grid container spacing={2} sx={styles.dialogFormGrid}>
              <Grid item xs={12} sm={12} md={12} lg={12}>
                <TextField
                  sx={styles.fullWidth}
                  color="primary"
                  disabled={!props.isOwner}
                  id="account-holder-field"
                  label="Ausbildung"
                  variant="outlined"
                  value={apprenticeship}
                  onChange={(event) => {
                    setApprenticeship(event.target.value);
                  }}
                />
                <Grid item xs={12} sm={12} md={12} lg={12}>
                  <Grid container spacing={1}>
                    <Grid item xs={12} sm={12} md={12} lg={12}>
                      <Typography>Sprachen:</Typography>
                    </Grid>
                    {languages.map((language, index) => {
                      return (
                        <Grid
                          item
                          container
                          spacing={1}
                          xs={11}
                          sm={8}
                          md={6}
                          lg={4}
                          sx={styles.dialogListItem}
                          key={index}
                        >
                          <Grid item xs={5}>
                            <Autocomplete
                              value={language.value}
                              onChange={(event, newValue) => {
                                if (typeof newValue === "string") {
                                  dispatchLanguages({
                                    type: membersTypes.languagesReducerActionType.addNewLanguageWithValueAsString,
                                    payload: { index, value: newValue },
                                  });
                                } else if ((newValue && newValue.inputValue) || newValue) {
                                  dispatchLanguages({
                                    type: membersTypes.languagesReducerActionType.addNewLanguageWithValueAsObject,
                                    payload: { index, lang: newValue },
                                  });
                                }
                              }}
                              filterOptions={(options, params) => {
                                const filtered = langFilter(options, params);

                                // Suggest the creation of a new value
                                if (params.inputValue !== "") {
                                  filtered.push({
                                    inputValue: params.inputValue,
                                    value: `"${params.inputValue}" hinzufügen`,
                                  });
                                }
                                return filtered;
                              }}
                              selectOnFocus
                              clearOnBlur
                              options={listOfLanguages}
                              getOptionLabel={(option) => {
                                if (typeof option === "string") {
                                  return option;
                                }
                                return option.value;
                              }}
                              freeSolo
                              renderInput={(params) => <TextField {...params} label="Sprache" variant="outlined" />}
                            />
                          </Grid>
                          <Grid item xs={5}>
                            <FormControl sx={styles.fullWidth}>
                              <InputLabel id="language-niveau-select-label">Niveau</InputLabel>
                              <Select
                                labelId="language-niveau-select-label"
                                value={language.level}
                                onChange={(event) => {
                                  dispatchLanguages({
                                    type: membersTypes.languagesReducerActionType.addNewLanguageWithNiveau,
                                    payload: {
                                      index,
                                      niveau: "" + event.target.value,
                                    },
                                  });
                                }}
                              >
                                <MenuItem value={5}>{getLanguageNiveauLabel(5)}</MenuItem>
                                <MenuItem value={4}>{getLanguageNiveauLabel(4)}</MenuItem>
                                <MenuItem value={3}>{getLanguageNiveauLabel(3)}</MenuItem>
                                <MenuItem value={2}>{getLanguageNiveauLabel(2)}</MenuItem>
                                <MenuItem value={1}>{getLanguageNiveauLabel(1)}</MenuItem>
                              </Select>
                            </FormControl>
                          </Grid>
                          <Grid item xs={2}>
                            <IconButton
                              aria-label="delete"
                              color="primary"
                              onClick={() =>
                                dispatchLanguages({
                                  type: membersTypes.languagesReducerActionType.deleteLanguage,
                                  payload: { lang: language },
                                })
                              }
                              size="large"
                            >
                              <Clear />
                            </IconButton>
                          </Grid>
                        </Grid>
                      );
                    })}
                    <Grid item xs={12} sm={2} md={2} lg={2} sx={styles.addListItemBtn}>
                      <IconButton
                        aria-label="add"
                        color="primary"
                        disabled={languages.some((lang) => lang.value === "" || lang.level === 0)}
                        onClick={() =>
                          dispatchLanguages({
                            type: membersTypes.languagesReducerActionType.addEmptyLanguage,
                          })
                        }
                        size="large"
                      >
                        <AddCircleOutline />
                      </IconButton>
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item xs={12} sm={12} md={12} lg={12}>
                  <Grid container spacing={1}>
                    <Grid item xs={12} sm={12} md={12} lg={12}>
                      <Typography>EDV-Kenntnisse:</Typography>
                    </Grid>
                    {itSkills.map((edv, index) => {
                      return (
                        <Grid
                          item
                          container
                          spacing={1}
                          xs={11}
                          sm={8}
                          md={6}
                          lg={4}
                          sx={styles.dialogListItem}
                          key={index}
                        >
                          <Grid item xs={5}>
                            <Autocomplete
                              value={edv.value}
                              onChange={(event, newValue) => {
                                if (typeof newValue === "string") {
                                  dispatchItSkills({
                                    type: membersTypes.itSkillsReducerActionType.addNewItSkillWithValueAsString,
                                    payload: { index, value: newValue },
                                  });
                                } else if ((newValue && newValue.inputValue) || newValue) {
                                  dispatchItSkills({
                                    type: membersTypes.itSkillsReducerActionType.addNewItSkillWithValueAsObject,
                                    payload: { index, itSkill: newValue },
                                  });
                                }
                              }}
                              filterOptions={(options, params) => {
                                const filtered = itFilter(options, params);

                                // Suggest the creation of a new value
                                if (params.inputValue !== "") {
                                  filtered.push({
                                    inputValue: params.inputValue,
                                    value: `"${params.inputValue}" hinzufügen`,
                                  });
                                }
                                return filtered;
                              }}
                              selectOnFocus
                              clearOnBlur
                              options={listOfEDVSkills}
                              getOptionLabel={(option) => {
                                if (typeof option === "string") {
                                  return option;
                                }
                                return option.value;
                              }}
                              freeSolo
                              renderInput={(params) => (
                                <TextField {...params} label="EDV-Kenntnis" variant="outlined" />
                              )}
                            />
                          </Grid>
                          <Grid item xs={5}>
                            <FormControl sx={styles.fullWidth}>
                              <InputLabel id="edv-skill-niveau-select-label">Niveau</InputLabel>
                              <Select
                                labelId="edv-skill-niveau-select-label"
                                value={edv.level}
                                onChange={(event) => {
                                  dispatchItSkills({
                                    type: membersTypes.itSkillsReducerActionType.addNewItSkillWithNiveau,
                                    payload: {
                                      index,
                                      level: "" + event.target.value,
                                    },
                                  });
                                }}
                              >
                                <MenuItem value={3}>{getEDVNiveauLabel(3)}</MenuItem>
                                <MenuItem value={2}>{getEDVNiveauLabel(2)}</MenuItem>
                                <MenuItem value={1}>{getEDVNiveauLabel(1)}</MenuItem>
                              </Select>
                            </FormControl>
                          </Grid>
                          <Grid item xs={2}>
                            <IconButton
                              aria-label="delete"
                              color="primary"
                              onClick={() =>
                                dispatchItSkills({
                                  type: membersTypes.itSkillsReducerActionType.deleteItSkill,
                                  payload: { itSkill: edv },
                                })
                              }
                              size="large"
                            >
                              <Clear />
                            </IconButton>
                          </Grid>
                        </Grid>
                      );
                    })}
                    <Grid item xs={12} sm={2} md={2} lg={2} sx={styles.addListItemBtn}>
                      <IconButton
                        aria-label="add"
                        color="primary"
                        disabled={itSkills.some((edv) => edv.value === "" || edv.level === 0)}
                        onClick={() => {
                          dispatchItSkills({
                            type: membersTypes.itSkillsReducerActionType.addEmptyItSkill,
                          });
                        }}
                        size="large"
                      >
                        <AddCircleOutline />
                      </IconButton>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12} sm={12} md={12} lg={12}>
                <hr />
              </Grid>
              <Grid item xs={12} sm={12} md={12} lg={12} sx={styles.submitContainer}>
                <Button sx={styles.cancelButton} variant="contained" onClick={handleQualificationInfoDialogClose}>
                  Abbrechen
                </Button>
                <Button sx={styles.submitButton} variant="contained" color="primary" type="submit">
                  Änderungen speichern
                </Button>
              </Grid>
            </Grid>
          </form>
        </DialogContent>
      </Dialog>
    );
  };

  /**
   * Renders the dialog for changing the payment informations
   */
  const renderPaymentInformationDialog = (): React.ReactNode => {
    return (
      <Dialog
        open={paymentInfoDialogOpen}
        onClose={handlePaymentInfoDialogClose}
        fullWidth
        maxWidth="lg"
        keepMounted
        aria-labelledby="payment-dialog-title"
      >
        <DialogTitle id="payment-dialog-title">Zahlungsinformationen</DialogTitle>
        <DialogContent sx={styles.dialogContent}>
          <form autoComplete="off" onSubmit={submit}>
            <Grid container spacing={2} sx={styles.dialogFormGrid}>
              <Grid item xs={12} sm={12} md={12} lg={12}>
                <TextField
                  sx={styles.fullWidth}
                  color="primary"
                  disabled={!props.isOwner}
                  id="account-holder-field"
                  label="Kontoinhaber"
                  variant="outlined"
                  value={accountHolder}
                  onChange={(event) => {
                    setAccountHolder(event.target.value);
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={12} md={12} lg={12}>
                <TextField
                  sx={styles.fullWidth}
                  color="primary"
                  disabled={!props.isOwner}
                  id="iban-field"
                  label="IBAN"
                  variant="outlined"
                  value={ibanState}
                  onChange={(event) => {
                    setIbanState(event.target.value);
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={12} md={12} lg={12}>
                <TextField
                  sx={styles.fullWidth}
                  color="primary"
                  disabled={!props.isOwner}
                  id="bic-field"
                  label="BIC"
                  variant="outlined"
                  value={bicState}
                  onChange={(event) => {
                    setBicState(event.target.value);
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={12} md={12} lg={12}>
                <hr />
              </Grid>
              <Grid item xs={12} sm={12} md={12} lg={12} sx={styles.submitContainer}>
                <Button sx={styles.cancelButton} variant="contained" onClick={handlePaymentInfoDialogClose}>
                  Abbrechen
                </Button>
                <Button sx={styles.submitButton} variant="contained" color="primary" type="submit">
                  Änderungen speichern
                </Button>
              </Grid>
            </Grid>
          </form>
        </DialogContent>
      </Dialog>
    );
  };

  return (
    <Box sx={styles.displayMemberDetailsRoot}>
      <Grid container spacing={3}>
        <>
          {renderDirectorPosition()}
          {renderImage()}
          {renderGeneralInformation()}
          {renderGeneralInformationDialog()}
          {renderClubInformation()}
          {renderClubInformationDialog()}
          {renderProjectList()}
          {renderWorkshopList()}
          {renderQualificationInformation()}
          {renderQualificationInformationDialog()}
          {renderPrivateAddressInformation()}
          {renderPrivateAddressInformationDialog()}
          {renderStudyInformation()}
          {renderStudyInformationDialog()}
          {renderPaymentInformation()}
          {renderPaymentInformationDialog()}
        </>
        <Grid item>
          <strong>Letzte Änderung: {memberDetails.lastChange?.toString()}</strong>
        </Grid>
      </Grid>
    </Box>
  );
};

export default DisplayMemberDetails;
